// The route table and the auth guard.
//
// ===========================================================================
// PAGE ELEMENT REGISTRATION
// ---------------------------------------------------------------------------
// Every route in the table below names a page element, and every one of those
// elements is now defined and imported here. **That invariant is the thing to
// preserve.** A custom element that has not been defined renders as an EMPTY
// element rather than throwing, so a missing import does not take the SPA down —
// it shows working chrome wrapped around nothing, which reads as a bug in the
// product rather than as a missing import. There is no longer any route in this
// table that is allowed to render nothing.
//
// Each page is a self-registering custom element — it calls
// `customElements.define` in its own module — so landing one is two edits:
//
//   1. write `src/pages/page-<name>.ts` with `@customElement("page-<name>")`
//   2. add `import "./pages/page-<name>.js";` to the block directly below
//
// Nothing else here changes. If you need a route that is NOT in the table,
// `routes` is a mutable export: push a RouteConfig onto it and call
// `router.goto(location.pathname)` — that is the documented extension point for
// the backoffice console, which owns its own sub-tree.
//
// (Add page imports here, alphabetically, as they land.)
// ===========================================================================

import { html } from "lit";
import type { BaseRouteConfig, RouteConfig } from "@lit-labs/router";
import { pb } from "./api/client.js";
import { navigate } from "./nav.js";

import "./pages/page-analytics.js";
import "./pages/page-api-keys.js";
import "./pages/page-audit.js";
import "./pages/page-audit-record.js";
import "./pages/page-backoffice.js";
import "./pages/page-datasource-editor.js";
import "./pages/page-datasources.js";
import "./pages/page-diff.js";
import "./pages/page-explain.js";
import "./pages/page-generation.js";
import "./pages/page-generation-yaml.js";
import "./pages/page-generations.js";
import "./pages/page-instance.js";
import "./pages/page-instance-jobs.js";
import "./pages/page-instance-new.js";
import "./pages/page-instance-settings.js";
import "./pages/page-instances.js";
import "./pages/page-invite.js";
import "./pages/page-job.js";
// The kit gallery. It is the one page that shipped before the product pages did,
// because it is how a human verifies the component kit with no backend running.
import "./pages/page-kit.js";
import "./pages/page-login.js";
import "./pages/page-onboarding.js";
import "./pages/page-org.js";
import "./pages/page-orgs.js";
import "./pages/page-playground.js";
import "./pages/page-policies.js";
import "./pages/page-policy-editor.js";
import "./pages/page-publish.js";
import "./pages/page-security.js";
import "./pages/page-server-config.js";
import "./pages/page-subject-binding.js";
import "./pages/page-subject-bindings.js";
import "./pages/page-tests.js";
import "./pages/page-validation.js";

import "./components/bn-empty-state.js";
import "./components/bn-button.js";

/**
 * guard is the auth gate. An anonymous caller hitting an authenticated route is
 * redirected to `/sign-in?next=<attempted path>`; returning false cancels the
 * in-progress navigation, because the redirect drives the new render. Opt a
 * route in with `enter: guard`.
 *
 * It checks only for a *valid-looking* stored token. The server is the
 * authority — every route re-checks membership and role — so this is a
 * user-experience gate, not a security control: it exists so an expired session
 * lands on the sign-in form instead of on a screen full of 401s.
 */
export function guard(): boolean {
  if (pb.authStore.isValid) return true;
  navigate("/sign-in?next=" + encodeURIComponent(location.pathname + location.search), {
    replace: true,
  });
  return false;
}

/**
 * routes is the full product route table.
 *
 * **Order is significant.** `@lit-labs/router` returns the *first* pattern that
 * matches, so a literal segment must precede the parameterized pattern it would
 * otherwise be swallowed by: `/instances/new` before `/instances/:id`,
 * `…/datasources/new` before `…/datasources/:name`. Those pairs are marked
 * below; do not re-sort the table.
 *
 * Ids in paths are the bare 15-character record ids the API returns. Nothing
 * parses, strips or validates a prefix — the `inst_7fq` shorthand in the
 * architecture documents is a reading convention, not the wire format.
 */
export const routes: RouteConfig[] = [
  // ── Entry ───────────────────────────────────────────────────────────────
  {
    // The product's home is the instance list: an instance is the object a
    // customer comes here to operate, and most accounts have exactly one
    // organization.
    path: "/",
    enter: guard,
    render: () => html`<page-instances></page-instances>`,
  },
  {
    // Sign-in is also sign-up: `POST request-otp` creates the account on first
    // request (`SC-F001`, `SC-F002`), so the page must not promise otherwise.
    path: "/sign-in",
    render: () => html`<page-sign-in></page-sign-in>`,
  },
  {
    // First-login onboarding: display name, optional passkey, first
    // organization. Guarded — it mutates the signed-in account.
    path: "/welcome",
    enter: guard,
    render: () => html`<page-onboarding></page-onboarding>`,
  },
  {
    // Invitation landing. **Deliberately UNGUARDED**: the link arrives by email
    // and the recipient may have no account at all. The page looks the token up
    // anonymously (`POST /v1/invites/lookup`) and sends them to
    // `/sign-in?next=…` itself. Guarding it would bounce every new invitee to a
    // sign-in form with no explanation of what they were invited to.
    path: "/invite",
    render: () => html`<page-invite></page-invite>`,
  },

  // ── Account ─────────────────────────────────────────────────────────────
  {
    // API keys are an ACCOUNT-level resource, not an organization one: a key
    // carries the owner's authority across every organization they belong to,
    // narrowed by its own scope. Hence no `:orgId` in this path.
    path: "/api-keys",
    enter: guard,
    render: () => html`<page-api-keys></page-api-keys>`,
  },
  {
    // Passkeys, sign-out-everywhere and the account's own sign-in history.
    path: "/security",
    enter: guard,
    render: () => html`<page-security></page-security>`,
  },

  // ── Organizations ───────────────────────────────────────────────────────
  {
    path: "/orgs",
    enter: guard,
    render: () => html`<page-orgs></page-orgs>`,
  },
  {
    // Members and invitations are TABS of `<page-org>`, not pages of their own:
    // the four organization surfaces share one record read, one role gate and
    // one `ETag`, and splitting them would mean three copies of that. The paths
    // stay because `SC-S101` wants each surface linkable — `<page-org>` reads
    // the trailing segment as its initial tab when `?tab=` is absent, and
    // rewrites the URL to the canonical `/orgs/:orgId?tab=…` on the next switch.
    path: "/orgs/:orgId/members",
    enter: guard,
    render: ({ orgId }) => html`<page-org .orgId=${orgId ?? ""}></page-org>`,
  },
  {
    path: "/orgs/:orgId/invitations",
    enter: guard,
    render: ({ orgId }) => html`<page-org .orgId=${orgId ?? ""}></page-org>`,
  },
  {
    // AFTER its children: `/orgs/:orgId` would otherwise never match, but the
    // reverse order would make `/orgs/:orgId` swallow nothing — the children are
    // three segments deep. Kept here for readability, not for correctness.
    path: "/orgs/:orgId",
    enter: guard,
    render: ({ orgId }) => html`<page-org .orgId=${orgId ?? ""}></page-org>`,
  },

  // ── Instances ───────────────────────────────────────────────────────────
  {
    path: "/instances",
    enter: guard,
    render: () => html`<page-instances></page-instances>`,
  },
  {
    // ORDER: must precede `/instances/:id`, which would match "new" as an id.
    path: "/instances/new",
    enter: guard,
    render: () => html`<page-instance-create></page-instance-create>`,
  },

  // ── The instance console. Each of these is one tab. ──────────────────────
  {
    path: "/instances/:id/datasources",
    enter: guard,
    render: ({ id }) => html`<page-datasources .instanceId=${id ?? ""}></page-datasources>`,
  },
  {
    // ORDER: must precede `…/datasources/:name`.
    path: "/instances/:id/datasources/new",
    enter: guard,
    render: ({ id }) =>
      html`<page-datasource-editor .instanceId=${id ?? ""} .create=${true}></page-datasource-editor>`,
  },
  {
    path: "/instances/:id/datasources/:name",
    enter: guard,
    render: ({ id, name }) =>
      html`<page-datasource-editor
        .instanceId=${id ?? ""}
        .name=${name ?? ""}
      ></page-datasource-editor>`,
  },
  {
    path: "/instances/:id/policies",
    enter: guard,
    render: ({ id }) => html`<page-policies .instanceId=${id ?? ""}></page-policies>`,
  },
  {
    // ORDER: must precede the `:kind/:name` pattern's shorter siblings. The kind
    // arrives as `?kind=`, because it is chosen inside the page rather than
    // being part of the identity of a record that does not exist yet.
    path: "/instances/:id/policies/new",
    enter: guard,
    render: ({ id }) =>
      html`<page-policy-editor .instanceId=${id ?? ""} .create=${true}></page-policy-editor>`,
  },
  {
    // Addressed by `(kind, name)` — Sluice's own uniqueness key across the whole
    // policy directory, and the same pair the API routes use. Not by record id:
    // a bookmarked policy URL should survive a re-import.
    path: "/instances/:id/policies/:kind/:name",
    enter: guard,
    render: ({ id, kind, name }) =>
      html`<page-policy-editor
        .instanceId=${id ?? ""}
        .kind=${kind ?? ""}
        .name=${name ?? ""}
      ></page-policy-editor>`,
  },
  {
    // SubjectBinding is one of the eleven kinds, but it gets its own surface:
    // it is where instance API keys are minted (`SC-F521`), which is a
    // credential lifecycle, not a policy form.
    path: "/instances/:id/subject-bindings",
    enter: guard,
    render: ({ id }) => html`<page-subject-bindings .instanceId=${id ?? ""}></page-subject-bindings>`,
  },
  {
    // ORDER: must precede `…/subject-bindings/:name`.
    path: "/instances/:id/subject-bindings/new",
    enter: guard,
    render: ({ id }) =>
      html`<page-subject-binding .instanceId=${id ?? ""} .create=${true}></page-subject-binding>`,
  },
  {
    path: "/instances/:id/subject-bindings/:name",
    enter: guard,
    render: ({ id, name }) =>
      html`<page-subject-binding
        .instanceId=${id ?? ""}
        .name=${name ?? ""}
      ></page-subject-binding>`,
  },
  {
    // The customer-owned half of `server.yaml` as a form, with the
    // control-plane-owned half read-only and reasoned (`SC-F612`, `SC-F613`,
    // `SC-F614`). Three policy editors link here for a server-settings change.
    path: "/instances/:id/server-config",
    enter: guard,
    render: ({ id }) => html`<page-server-config .instanceId=${id ?? ""}></page-server-config>`,
  },
  {
    // The draft review screen and the publish confirmation are one surface:
    // `GET .../draft` is literally the screen behind the publish button, and it
    // carries the co-author roll-up the confirmation must show.
    path: "/instances/:id/publish",
    enter: guard,
    render: ({ id }) => html`<page-publish .instanceId=${id ?? ""}></page-publish>`,
  },
  {
    path: "/instances/:id/generations",
    enter: guard,
    render: ({ id }) => html`<page-generations .instanceId=${id ?? ""}></page-generations>`,
  },
  {
    path: "/instances/:id/generations/:gen/yaml",
    enter: guard,
    render: ({ id, gen }) =>
      html`<page-generation-yaml
        .instanceId=${id ?? ""}
        .generation=${gen ?? ""}
      ></page-generation-yaml>`,
  },
  {
    path: "/instances/:id/generations/:gen",
    enter: guard,
    render: ({ id, gen }) =>
      html`<page-generation .instanceId=${id ?? ""} .generation=${gen ?? ""}></page-generation>`,
  },
  {
    // `?from=` and `?to=` carry the two refs (`draft` | `live` | a number), the
    // same spelling the API takes, so a diff URL is shareable.
    path: "/instances/:id/diff",
    enter: guard,
    render: ({ id }) => html`<page-diff .instanceId=${id ?? ""}></page-diff>`,
  },
  {
    path: "/instances/:id/validation",
    enter: guard,
    render: ({ id }) => html`<page-validation .instanceId=${id ?? ""}></page-validation>`,
  },
  {
    path: "/instances/:id/explain",
    enter: guard,
    render: ({ id }) => html`<page-explain .instanceId=${id ?? ""}></page-explain>`,
  },
  {
    path: "/instances/:id/tests",
    enter: guard,
    render: ({ id }) => html`<page-tests .instanceId=${id ?? ""}></page-tests>`,
  },
  {
    // The one surface in the product that can return customer rows. It is
    // maintainer-or-owner, step-up gated, refused to every scoped token, and
    // audited on every use including failures (`SC-F714`).
    path: "/instances/:id/playground",
    enter: guard,
    render: ({ id }) => html`<page-playground .instanceId=${id ?? ""}></page-playground>`,
  },
  {
    path: "/instances/:id/audit",
    enter: guard,
    render: ({ id }) => html`<page-audit .instanceId=${id ?? ""}></page-audit>`,
  },
  {
    path: "/instances/:id/audit/:recordId",
    enter: guard,
    render: ({ id, recordId }) =>
      html`<page-audit-record
        .instanceId=${id ?? ""}
        .recordId=${recordId ?? ""}
      ></page-audit-record>`,
  },
  {
    path: "/instances/:id/analytics",
    enter: guard,
    render: ({ id }) => html`<page-analytics .instanceId=${id ?? ""}></page-analytics>`,
  },
  {
    // Job history for this instance. The `SC-F332` job resource is one model for
    // every long operation, so this is one tab and not five. The console's
    // Operations panel is the same resource under a smaller limit; this is the
    // paged, filtered history behind it.
    path: "/instances/:id/jobs",
    enter: guard,
    render: ({ id }) => html`<page-instance-jobs .instanceId=${id ?? ""}></page-instance-jobs>`,
  },
  {
    // Display name, size class (`SC-F309`), audit privacy profile (`SC-F821`) and
    // the danger zone. The console's settings tab renders the same fields read-only
    // and links here; the destructive ceremonies stay on the console's danger tab,
    // which owns them.
    path: "/instances/:id/settings",
    enter: guard,
    render: ({ id }) =>
      html`<page-instance-settings .instanceId=${id ?? ""}></page-instance-settings>`,
  },
  {
    // AFTER every `/instances/:id/*` tab, so the overview does not swallow them.
    // (URLPattern would not in fact match a longer path against this pattern,
    // but keeping the overview last makes the tab set readable as a group.)
    path: "/instances/:id",
    enter: guard,
    render: ({ id }) => html`<page-instance .instanceId=${id ?? ""}></page-instance>`,
  },

  // ── Jobs ────────────────────────────────────────────────────────────────
  {
    // A shareable link to one job — what a support conversation about a stuck
    // publish points at, and the most-linked route in the product after the
    // instance console: nine surfaces render an "Open job" link. Every one of them
    // also watches the job inline through `watchJob`, so this page is built for the
    // COLD open — opened hours later, from an email, by somebody who was not the
    // actor. See its own module doc.
    path: "/jobs/:jobId",
    enter: guard,
    render: ({ jobId }) => html`<page-job .jobId=${jobId ?? ""}></page-job>`,
  },

  // ── Development surfaces ────────────────────────────────────────────────
  {
    // The component gallery. **Deliberately UNGUARDED**: its whole purpose is to
    // render the kit in both themes with no session, no backend and no seeded
    // database, and a guard would bounce it to a sign-in form. It reads no API
    // and shows no customer data — every value on it is a fixture in the page
    // module. The leading underscore marks it as a development surface, which is
    // why it appears in no nav item.
    path: "/_kit",
    render: () => html`<page-kit></page-kit>`,
  },

  // ── Backoffice ──────────────────────────────────────────────────────────
  {
    // Staff console (`SC-F903`). Guarded like everything else, but the guard is
    // not the control: every `/backoffice/*` API route is gated by a
    // staff-principal check in middleware that refuses **every** customer
    // credential class unconditionally. Route-map absence protects scoped tokens
    // only; the staff check is what protects this surface.
    //
    // The console owns its own sub-tree. Push additional `/backoffice/…` routes
    // onto `routes` from the backoffice module rather than growing this table.
    path: "/backoffice",
    enter: guard,
    render: () => html`<page-backoffice></page-backoffice>`,
  },
];

/**
 * fallback renders for any unmatched path. It is built from the component kit
 * rather than a dedicated page element so that it works from the first commit —
 * a not-found screen that itself fails to render is a bad first impression of a
 * routing bug.
 */
export const fallback: BaseRouteConfig = {
  render: () => html`
    <bn-empty-state
      icon="∅"
      heading="Page not found"
      message="The page you are looking for does not exist, or the link that brought you here is out of date."
    >
      <bn-button slot="actions" variant="primary" @click=${() => navigate("/")}>
        Go to instances
      </bn-button>
    </bn-empty-state>
  `,
};
